// ─────────────────────────────────────────────────────────────────────────────
// INSTRUMENT: Situational Leadership Assessment
// 16 Situational Judgment items — each maps to one of S1/S2/S3/S4
// ─────────────────────────────────────────────────────────────────────────────

export const QUESTIONS = [
  {
    id:1,
    context:"A new graduate has just joined your team. It is their first week and they have been given a client presentation to prepare in 5 days. They are enthusiastic and ask almost no questions.",
    text:"How do you respond?",
    options:[
      {text:"Give them full ownership and check in at the end of the week. You do not want to overwhelm them.", style:"S4"},
      {text:"Walk them through the presentation structure step by step, set daily check-ins, and review drafts closely.", style:"S1"},
      {text:"Ask them how they are planning to approach it, share your perspective, and agree a check-in mid-week.", style:"S2"},
      {text:"Tell them the expected outcome and offer to be available if they have questions.", style:"S3"},
    ]
  },
  {
    id:2,
    context:"A high-performing senior analyst has been on your team for 3 years. She knows the process cold and has never missed a deadline. You have just assigned her the quarterly report.",
    text:"How do you approach this?",
    options:[
      {text:"Walk her through the requirements and timeline to make sure she has not missed anything.", style:"S1"},
      {text:"Assign it with the deadline. Check in once at the midpoint. Trust her to deliver.", style:"S4"},
      {text:"Discuss the project scope together, share your goals, and offer to be available for questions.", style:"S2"},
      {text:"Check in frequently — the report affects many stakeholders and you want to stay close.", style:"S1"},
    ]
  },
  {
    id:3,
    context:"A team member who is technically excellent has been underperforming on a new responsibility. He seems hesitant to make decisions independently and keeps asking for your approval even on routine items.",
    text:"What is your leadership response?",
    options:[
      {text:"Tell him exactly what decisions he should be making on his own and monitor his output closely.", style:"S1"},
      {text:"Have a direct conversation about his performance gap and give him a clear improvement plan.", style:"S2"},
      {text:"Have a supportive conversation to understand what is making him hesitant. Affirm his capability and encourage him to trust his own judgment.", style:"S3"},
      {text:"Give him more stretch assignments to build confidence by forcing him to deliver independently.", style:"S4"},
    ]
  },
  {
    id:4,
    context:"An employee who was previously high-performing has recently disengaged. He makes cynical comments in team meetings and shows little initiative. He joined with high enthusiasm 14 months ago.",
    text:"How do you respond?",
    options:[
      {text:"Hold a private conversation to understand what changed, listen without judgment, and explore what would re-engage him.", style:"S3"},
      {text:"Address the behaviour directly and give him clear expectations about professionalism in meetings.", style:"S1"},
      {text:"Assign him a high-visibility project that plays to his strengths and step back to see if he rises to it.", style:"S4"},
      {text:"Explain your expectations, acknowledge his past contributions, and discuss what has changed together.", style:"S2"},
    ]
  },
  {
    id:5,
    context:"Your most experienced team member is leading a project that is behind schedule. She has not raised any concerns and gives positive updates in weekly calls, but you can see the timeline slipping.",
    text:"What do you do?",
    options:[
      {text:"Take the project back under closer supervision until it is back on track.", style:"S1"},
      {text:"Ask for a one-on-one to review the plan in detail. Provide hands-on direction to close the gap.", style:"S2"},
      {text:"Raise your concern directly in a private conversation. Ask what support she needs and offer specific resources.", style:"S3"},
      {text:"Trust her to manage it — she has done this before. Step in only if the deadline is formally missed.", style:"S4"},
    ]
  },
  {
    id:6,
    context:"A new team member has just completed their first independent task. The output is 80% of what you would expect. They are clearly proud of the work.",
    text:"How do you handle the feedback conversation?",
    options:[
      {text:"Praise the effort and tell them it is good. You do not want to knock their confidence this early.", style:"S4"},
      {text:"Give specific positive feedback on what worked, then walk through exactly what needs to change and why, and show them how.", style:"S1"},
      {text:"Ask them to review it themselves first. What do they think worked? What would they change? Then add your perspective.", style:"S3"},
      {text:"Acknowledge their effort, give detailed feedback on improvements, and explain the reasoning behind each point. Set a revision target together.", style:"S2"},
    ]
  },
  {
    id:7,
    context:"A mid-level manager on your team has been given a new responsibility — managing a cross-functional project for the first time. She has managed teams before but has no experience with cross-functional dynamics.",
    text:"What is your approach?",
    options:[
      {text:"Give her the brief and let her figure it out. She is a capable manager — cross-functional is not that different.", style:"S4"},
      {text:"Set clear expectations for the project, have weekly check-ins, and walk her through cross-functional dynamics she will encounter.", style:"S1"},
      {text:"Have a detailed kick-off: align on expectations, share your experience with cross-functional challenges, and set up regular touchpoints where she can bring problems to you.", style:"S2"},
      {text:"Check in occasionally and offer to be a sounding board if she wants to talk through decisions.", style:"S3"},
    ]
  },
  {
    id:8,
    context:"Your team is under significant pressure and one member is taking on too much and showing signs of stress. His work quality is still high, but he looks burnt out.",
    text:"How do you respond?",
    options:[
      {text:"Restructure his workload for him. Clearly define what stays, what gets transferred, and what gets dropped.", style:"S1"},
      {text:"Raise your observation directly and discuss together what can be reprioritised or redistributed. Offer specific support.", style:"S2"},
      {text:"Have a private conversation to acknowledge what you are observing, ask how he is doing, and let him lead the conversation about what he needs.", style:"S3"},
      {text:"Keep an eye on the quality metrics. He is experienced enough to manage his own capacity — if it becomes a problem he will flag it.", style:"S4"},
    ]
  },
  {
    id:9,
    context:"A junior team member has just been told she failed her first major deliverable. She is visibly upset and says she does not know if she is right for this role.",
    text:"What is your first response?",
    options:[
      {text:"Tell her this happens to everyone and focus the conversation on what she needs to do differently next time.", style:"S1"},
      {text:"Listen first. Let her express her feelings. Then work through specifically what went wrong and what to do differently — with your support throughout.", style:"S2"},
      {text:"Ask her what she thinks went wrong and what she would change. Support her in building her own answer.", style:"S3"},
      {text:"Give her space and check in the next day when emotions have settled.", style:"S4"},
    ]
  },
  {
    id:10,
    context:"You are preparing your team for a critical new client pitch. One team member is a brilliant analyst but has never presented to a senior external audience before.",
    text:"How do you prepare her?",
    options:[
      {text:"Brief her on the client, define her section precisely, do a full rehearsal with specific feedback, and be present on the day.", style:"S1"},
      {text:"She knows the material cold — brief her on the client context and trust her to present it well.", style:"S4"},
      {text:"Sit with her to build the section together, discuss the audience dynamics, rehearse twice, and debrief afterwards.", style:"S2"},
      {text:"Offer to be available if she wants to rehearse. Tell her you will be there for questions if she gets stuck.", style:"S3"},
    ]
  },
  {
    id:11,
    context:"A long-term team member tells you in a one-on-one that he wants to grow into a management role. You have seen him as a strong individual contributor but have not thought of him as leadership material yet.",
    text:"How do you respond?",
    options:[
      {text:"Be direct about what you have observed and give him a specific development plan with what he would need to demonstrate first.", style:"S1"},
      {text:"Ask him to tell you more about his interest. Explore what management means to him. Share your honest observations and agree on next steps together.", style:"S2"},
      {text:"Encourage the aspiration and ask what he thinks he needs to develop. Offer to be a sounding board as he works toward it.", style:"S3"},
      {text:"Point him toward relevant reading and tell him to look for opportunities to demonstrate leadership on current projects.", style:"S4"},
    ]
  },
  {
    id:12,
    context:"You have just delegated a full workstream to a capable team lead. Three weeks in, a stakeholder tells you they are concerned about the lead's communication style. You have not seen any problems yourself.",
    text:"What do you do?",
    options:[
      {text:"Bring the lead back into closer supervision and review her communications before they go out.", style:"S1"},
      {text:"Have a direct conversation with the lead: share the feedback, give specific guidance on what to change, and follow up weekly.", style:"S2"},
      {text:"Have a private conversation with the lead. Share the feedback you received, ask for her perspective, and let her determine how she wants to address it.", style:"S3"},
      {text:"Trust the lead to manage stakeholder relationships. One concern does not warrant intervention.", style:"S4"},
    ]
  },
  {
    id:13,
    context:"A team member has just delivered a project significantly over budget. In the debrief he acknowledges the overspend but seems to think it was justified by the quality of output.",
    text:"How do you handle the conversation?",
    options:[
      {text:"Clearly explain why the budget overrun is unacceptable regardless of output quality. Give him specific rules for the next project.", style:"S1"},
      {text:"Explain your perspective on why budget discipline matters, hear his reasoning fully, and agree on a clear process going forward.", style:"S2"},
      {text:"Ask him to walk you through his decision-making on cost. Explore the gap in perspective together and let him arrive at the right conclusion.", style:"S3"},
      {text:"Let it go this time — the output was good and over-correcting will make him overly cautious.", style:"S4"},
    ]
  },
  {
    id:14,
    context:"You have an experienced specialist who always delivers excellent work. She has recently started asking for approval on decisions that are clearly within her authority.",
    text:"What is your interpretation and response?",
    options:[
      {text:"She may have lost confidence for some reason. Have a genuine conversation to understand what has changed and affirm your trust in her judgment.", style:"S3"},
      {text:"Remind her clearly of her decision-making authority. Give her a list of what is and is not within her mandate.", style:"S1"},
      {text:"Something may have shifted in her context. Ask what is going on, share your observations, and discuss how to get her back to full autonomy.", style:"S2"},
      {text:"Keep approving her decisions for now — she will regain her footing without you making it an issue.", style:"S4"},
    ]
  },
  {
    id:15,
    context:"A team member has just completed a significant stretch assignment with solid results. It is the most complex thing she has done in this role.",
    text:"How do you close out the conversation?",
    options:[
      {text:"Document the learnings formally and set up a review of what could have gone better.", style:"S1"},
      {text:"Acknowledge the achievement specifically, explore what she learned, and discuss what stretch you might give her next.", style:"S3"},
      {text:"Praise the results and move on — she knows she did well.", style:"S4"},
      {text:"Give detailed feedback on what worked and what to develop further. Use it to build a development roadmap together.", style:"S2"},
    ]
  },
  {
    id:16,
    context:"You are onboarding a highly experienced external hire into a senior role. She comes with an excellent track record. It is her second week.",
    text:"How much structure and oversight do you provide?",
    options:[
      {text:"Treat her as fully autonomous from day one. She has earned autonomy through her track record.", style:"S4"},
      {text:"Give her a clear 30-60-90 day plan, schedule weekly alignment sessions, and be explicit about how decisions are made here — her competence does not transfer to your context automatically.", style:"S1"},
      {text:"Set a 90-day plan together. Discuss how your context differs from her previous experience, align on priorities, and check in weekly, gradually stepping back as she settles.", style:"S2"},
      {text:"Share context, introduce her to key stakeholders, and let her observe before taking on major responsibilities.", style:"S3"},
    ]
  },
];

export const QUESTIONS_PER_PAGE = 4;
export const TOTAL_PAGES = Math.ceil(QUESTIONS.length / QUESTIONS_PER_PAGE);
export default QUESTIONS;
